/**
 * State 9 of 9 — last successful sync (spec 01 §18, `PRODUCT.md` §4).
 *
 * **Driven by two timestamps, and the component refuses to render one.** Spec 01
 * §18 asks for "Last successful sync (agent sync + achau sync)", and `PRODUCT.md` §4
 * says why the plural matters: "a single 'last updated' hides which one is stale".
 * They fail independently and they are fixed by different people — a dead Agent is
 * IT's, a failing achau sweep is the platform's — so both props are required and
 * `null` is a rendered value rather than a hidden row.
 *
 * Where each comes from:
 *
 *   agentSyncAt  the newest `Device.last_heartbeat_at` in the screen's scope
 *                (`GET /devices`, `GET /devices/{id}/health`).
 *   achauSyncAt  `AchauSyncState.last_success_at` for the relevant `sync_key`
 *                (`users`, `companies`, `departments`, `tasks`), returned by
 *                `POST /api/v1/admin/sync`.
 *
 * ── Why the shell does not render one ───────────────────────────────────────
 *
 * There is no `GET` for `wp_sync_state` in `openapi.yaml`; the sweep state comes
 * back from a `POST` that needs `manage_policies`. A shell-wide "last synced" line
 * would therefore either be invented or be visible to one role, so this is a
 * component screens place with the timestamps they have already read, and
 * `DESIGN.md` §8's "every screen has a persistent last-updated line" is met per
 * screen. Written down because an absence in the frame otherwise reads as an
 * oversight.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { formatInstantWithAge } from '../i18n/datetime';
import { Icon } from './icons';

export interface LastSyncProps {
  /** Newest agent heartbeat in scope. `null` renders "chưa từng", never a blank. */
  readonly agentSyncAt: string | null;
  /** `AchauSyncState.last_success_at`. `null` renders "chưa từng". */
  readonly achauSyncAt: string | null;
  /** The company time zone from `SessionUser.timezone`. */
  readonly timezone: string;
  readonly testId?: string;
}

export function LastSync({
  agentSyncAt,
  achauSyncAt,
  timezone,
  testId = 'state-last-sync',
}: LastSyncProps): ReactNode {
  const { t, locale } = useI18n();
  const never = t('common.never');
  return (
    <p className="wp-lastsync" data-testid={testId}>
      <Icon name="clock" />
      <span className="wp-visually-hidden">{t('state.lastSync.title')}</span>
      <span className="wp-lastsync__item">
        <span className="wp-lastsync__label">{t('state.lastSync.agent')}:</span>
        <span>{formatInstantWithAge(agentSyncAt, locale, timezone, never)}</span>
      </span>
      <span className="wp-lastsync__item">
        <span className="wp-lastsync__label">{t('state.lastSync.achau')}:</span>
        <span>{formatInstantWithAge(achauSyncAt, locale, timezone, never)}</span>
      </span>
    </p>
  );
}
